import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  FormHelperText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import ProductDataService from '../../services/product.service';
import { useTranslation } from '../../hooks/useTranslation';

const ProductForm = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    tracking: 'FIFO',
    image: null,
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { t } = useTranslation();

  useEffect(() => {
    if (isEdit) {
      fetchProduct();
    }
  }, [id, isEdit]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await ProductDataService.get(id);
      const product = response.data?.data;
      // Format price to 2 decimal places when loading
      const formattedPrice = product.price ? parseFloat(product.price).toFixed(2) : '';
      setFormData({
        name: product.name || '',
        price: formattedPrice,
        tracking: product.tracking || 'FIFO',
        image: null,
      });
      if (product.image_url || product.image) {
        const imageUrl = product.image_url || `http://localhost:8080/storage/${product.image}`;
        setPreviewImage(imageUrl);
      }
    } catch (error) {
      console.error('Error loading product:', error);
      showSnackbar(t('products.errorLoadingProduct'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Limit price to 2 decimal places
    if (name === 'price') {
      // Allow only numbers and one decimal point
      const regex = /^\d*\.?\d{0,2}$/;
      if (value === '' || regex.test(value)) {
        setFormData(prev => ({ ...prev, [name]: value }));
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, image: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.price) {
      showSnackbar(t('products.pleaseFillRequiredFields'), 'error');
      return;
    }

    try {
      setLoading(true);
      // Validate and format price
      const priceValue = parseFloat(formData.price);
      if (isNaN(priceValue) || priceValue < 0) {
        showSnackbar(t('products.pleaseEnterValidPrice'), 'error');
        setLoading(false);
        return;
      }
      
      // Prepare data - send price as number, only include image if it's a File object
      const submitData = {
        name: formData.name.trim(), // Trim whitespace
        price: priceValue.toFixed(2), // Format to 2 decimals as string for FormData
        tracking: formData.tracking || 'FIFO',
        // Only include image if it's a File (new upload), not if it's null
        ...(formData.image instanceof File && { image: formData.image })
      };
      
      // Debug: log what we're sending
      console.log('Submitting data:', submitData);
      
      if (isEdit) {
        await ProductDataService.update(id, submitData);
        showSnackbar(t('products.productUpdatedSuccess'));
      } else {
        await ProductDataService.create(submitData);
        showSnackbar(t('products.productCreatedSuccess'));
      }
      navigate('/products');
    } catch (error) {
      console.error('Error saving product:', error);
      console.error('Error response:', error.response?.data);
      
      // Extract detailed error messages
      let errorMessage = `Error ${isEdit ? 'updating' : 'creating'} product`;
      
      if (error.response?.data?.errors) {
        // Laravel validation errors
        const errors = error.response.data.errors;
        const errorMessages = Object.entries(errors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('; ');
        errorMessage = errorMessages || errorMessage;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading && isEdit) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={() => navigate('/products')}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h5">
              {isEdit ? t('products.editProduct') : t('products.newProduct')}
            </Typography>
          </Box>
        }
        action={
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={loading}
          >
            {isEdit ? t('common.update') : t('common.save')} {t('products.title')}
          </Button>
        }
      />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('products.productName')}
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t('products.price')}
              name="price"
              type="text"
              value={formData.price}
              onChange={handleInputChange}
              inputProps={{ 
                inputMode: 'decimal',
                pattern: '[0-9]*\\.?[0-9]{0,2}'
              }}
              helperText={t('products.PriceHelperText')}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>{t('inventories.Tracking')}</InputLabel>
              <Select
                name="tracking"
                value={formData.tracking}
                onChange={handleInputChange}
                label={t('inventories.Tracking')}
              >
                <MenuItem value="FIFO">FIFO</MenuItem>
                <MenuItem value="LIFO">LIFO</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="file"
              label={t('products.ProductImage')}
              InputLabelProps={{ shrink: true }}
              inputProps={{ accept: 'image/*' }}
              onChange={handleImageChange}
            />
            <FormHelperText>
              <Box display="flex" alignItems="center" gap={0.5} component="span">
                <span>{t('products.imageHelperText')}</span>
                <Tooltip title={t('products.imageDimensionsTooltip')} arrow placement="top">
                  <InfoIcon color="action" fontSize="small" sx={{ cursor: 'help', ml: 0.5 }} />
                </Tooltip>
              </Box>
            </FormHelperText>
            {previewImage && (
              <Box mt={2}>
                <img
                  src={previewImage}
                  alt="Preview"
                  style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'cover' }}
                />
              </Box>
            )}
          </Grid>
        </Grid>
      </CardContent>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default ProductForm;
